/**
 * Servicio de encriptación client-side para documentos sensibles.
 * Usa Web Crypto API (AES-256-GCM) — nativa del browser, sin dependencias externas.
 *
 * Flujo:
 *  1. Antes de subir: generateKey() → encrypt(file, key) → upload blob cifrado
 *  2. Al mostrar:     fetch blob cifrado → decrypt(blob, key) → objectURL
 *
 * La clave (exportada como base64) se almacena en Firestore, protegida por security rules.
 * El IV se prepende al blob cifrado (primeros 12 bytes).
 */

const ALGORITHM = "AES-GCM";
const KEY_LENGTH = 256;
const IV_LENGTH = 12; // 96 bits recomendado para AES-GCM

export interface EncryptionMeta {
  /** Clave AES-256 exportada como base64 */
  key: string;
  /** Algoritmo usado (para futuras migraciones) */
  alg: "AES-256-GCM";
}

/**
 * Genera una clave AES-256 aleatoria y la exporta como base64.
 */
export async function generateEncryptionKey(): Promise<{
  cryptoKey: CryptoKey;
  exportedKey: string;
}> {
  const cryptoKey = await crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true, // extractable para poder exportar
    ["encrypt", "decrypt"],
  );

  const rawKey = await crypto.subtle.exportKey("raw", cryptoKey);
  const exportedKey = arrayBufferToBase64(rawKey);

  return { cryptoKey, exportedKey };
}

/**
 * Importa una clave AES-256 desde base64.
 */
async function importKey(base64Key: string): Promise<CryptoKey> {
  const rawKey = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ["decrypt"],
  );
}

/**
 * Encripta un File/Blob con AES-256-GCM.
 * Retorna un Blob con [IV (12 bytes) | ciphertext].
 */
export async function encryptFile(
  file: File | Blob,
  cryptoKey: CryptoKey,
): Promise<Blob> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plaintext = await file.arrayBuffer();

  const ciphertext = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    cryptoKey,
    plaintext,
  );

  // Prepend IV al ciphertext
  const combined = new Uint8Array(IV_LENGTH + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), IV_LENGTH);

  return new Blob([combined], { type: "application/octet-stream" });
}

/**
 * Desencripta un blob [IV | ciphertext] con AES-256-GCM.
 * Retorna el contenido original como Blob con el contentType especificado.
 */
export async function decryptBlob(
  encryptedData: ArrayBuffer,
  base64Key: string,
  contentType: string = "application/octet-stream",
): Promise<Blob> {
  const data = new Uint8Array(encryptedData);

  if (data.byteLength <= IV_LENGTH) {
    throw new Error("Datos encriptados inválidos: demasiado cortos");
  }

  const iv = data.slice(0, IV_LENGTH);
  const ciphertext = data.slice(IV_LENGTH);

  const cryptoKey = await importKey(base64Key);

  const plaintext = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    cryptoKey,
    ciphertext,
  );

  return new Blob([plaintext], { type: contentType });
}

/**
 * Descarga un archivo encriptado desde una URL, lo desencripta y retorna un object URL.
 * Incluye cache en memoria para evitar descargas repetidas.
 */
const decryptionCache = new Map<string, string>();

export async function getDecryptedObjectUrl(
  encryptedUrl: string,
  base64Key: string,
  contentType: string = "image/jpeg",
): Promise<string> {
  const cacheKey = `${encryptedUrl}:${base64Key}`;

  const cached = decryptionCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const response = await fetch(encryptedUrl);
  if (!response.ok) {
    throw new Error(`Error descargando archivo: ${response.status}`);
  }

  const encryptedData = await response.arrayBuffer();
  const decryptedBlob = await decryptBlob(
    encryptedData,
    base64Key,
    contentType,
  );
  const objectUrl = URL.createObjectURL(decryptedBlob);

  decryptionCache.set(cacheKey, objectUrl);

  return objectUrl;
}

/**
 * Limpia una URL de objeto del cache de desencriptación.
 */
export function revokeDecryptedUrl(
  encryptedUrl: string,
  base64Key: string,
): void {
  const cacheKey = `${encryptedUrl}:${base64Key}`;
  const cached = decryptionCache.get(cacheKey);
  if (cached) {
    URL.revokeObjectURL(cached);
    decryptionCache.delete(cacheKey);
  }
}

// ==================== HELPERS ====================

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}
